import { JAMSIL_MAP_IDS } from '@/constants/app';
import { useConfigStore } from '@/stores';
import { applyMapLanguageToMap, getFloorMapName, resolveMapFloorName } from '@/utils/floor-guide';
import { getAllFloorsFromIndoorBuildings, getUniqueFloorsByKoreanName } from '@/utils/map';
import type { FloorInfo } from '@/services/dto';
import { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useFloor = (newMap?: DabeeoMap, mapData?: DabeeoMapData) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedFloorName, setSelectedFLoorName] = useState<string>('');

    const floorCode = useConfigStore((state) => state?.config?.server?.floor?.floorCode);
    const floorNameKo = useConfigStore((state) => state?.config?.server?.floor?.name?.ko);
    const language = useConfigStore((state) => state?.config?.ui?.language);
    const buildings = useConfigStore((state) => state?.config?.server?.store?.buildings);
    const mapId = useConfigStore((state) => state?.config?.server?.store?.studioMapId) || '';

    const syncMapLanguage = useCallback(() => {
        applyMapLanguageToMap(newMap, language);
    }, [newMap, language]);

    const allFloors = useMemo(() => {
        const buildings = mapData?.dataBuilding?.getBuildings?.();
        return getAllFloorsFromIndoorBuildings(buildings);
    }, [mapData?.dataBuilding]);

    const floors = useMemo(() => {
        return getUniqueFloorsByKoreanName(allFloors);
    }, [allFloors]);

    const getFloorsByBuildingId = useCallback(
        (buildingId: string) => {
            if (!buildings) {
                return [];
            }
            const buildingFloors = buildings.find((building) => building.studioBuildingId === buildingId)?.floors;
            if (!buildingFloors?.length) {
                return [];
            }

            return [...buildingFloors].sort((a, b) => a.order - b.order);
        },
        [buildings]
    );

    const resolveFloorNameById = useCallback(
        (floorId?: string) => {
            if (!floorId) {
                return '';
            }

            const mapFloor = allFloors.find((floor) => floor.id === floorId);
            if (mapFloor) {
                return resolveMapFloorName(mapFloor.name);
            }

            const storeFloor = buildings?.flatMap((building) => building.floors ?? []).find((floor) => floor.floor_code === floorId);
            return storeFloor ? getFloorMapName(storeFloor) : '';
        },
        [allFloors, buildings]
    );

    const handleChangeFloorByName = useCallback(
        async (floorName?: string) => {
            try {
                if (!newMap || !floorName) return;
                setLoading(true);
                // syncMapLanguage();
                await newMap.context?.changeFloorByName(floorName || '1F');
                setSelectedFLoorName(floorName);
                syncMapLanguage();
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [newMap, syncMapLanguage]
    );

    const handleChangeFloor = useCallback(
        async (floorId?: string) => {
            try {
                if (!newMap || !floorId) return;
                setLoading(true);
                syncMapLanguage();
                await newMap.context?.changeFloor(floorId ?? '');
                const floorName = resolveFloorNameById(floorId);
                if (floorName) {
                    setSelectedFLoorName(floorName);
                }
                syncMapLanguage();
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [newMap, resolveFloorNameById, syncMapLanguage]
    );

    const isJamsil = JAMSIL_MAP_IDS.includes(mapId);
    const shouldChangeFloorByName = useMemo(
        () => !isJamsil && Array.isArray(buildings) && buildings.length > 1,
        [buildings, isJamsil]
    );

    const handleSelectStoreFloor = useCallback(
        async (floor?: FloorInfo) => {
            if (!floor) {
                return;
            }

            if (shouldChangeFloorByName) {
                const floorName = getFloorMapName(floor);
                if (!floorName) {
                    return;
                }
                await handleChangeFloorByName(floorName);
                return;
            }

            if (!floor.floor_code) {
                return;
            }

            await handleChangeFloor(floor.floor_code);
        },
        [shouldChangeFloorByName, handleChangeFloor, handleChangeFloorByName]
    );

    const selectedFloor = useMemo(() => {
        if (!Array.isArray(floors) || !floors?.length) {
            return null;
        }
        return floors.find((floor) => resolveMapFloorName(floor?.name) === selectedFloorName);
    }, [floors, selectedFloorName]);

    const getFloorById = useCallback(
        (floorId?: string) => {
            if (!Array.isArray(allFloors) || !allFloors?.length) {
                return undefined;
            }
            return allFloors.find((floor) => floor.id === floorId);
        },
        [allFloors]
    );

    useEffect(() => {
        if (!Array.isArray(floors)) {
            return;
        }
        if (floorCode) {
            setSelectedFLoorName(floorNameKo || '');
            return;
        }
        setSelectedFLoorName(resolveMapFloorName(floors.find((floor) => floor.defaultYn)?.name) || '');
    }, [floors, floorCode, floorNameKo]);

    return {
        floors,
        handleChangeFloor,
        loading,
        selectedFloor,
        getFloorById,
        handleChangeFloorByName,
        handleSelectStoreFloor,
        selectedFloorName,
        setSelectedFLoorName,
        getFloorsByBuildingId,
        syncMapLanguage,
    };
};
